import { useState, useEffect } from "react"
import Modal from "../Modal"
import Button from "../Button"
import api from "../../services/api"

// ListOrganizationsRequest caps per_page at 200.
const ORG_PICKER_PAGE_SIZE = 200

const ChangePlanOrganizationModal = ({ isOpen, onClose, onSuccess, plan }) => {
  const [organizations, setOrganizations] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const currentOrgId = plan?.organization?.id || plan?.created_by?.organization?.id

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setError(null)
    setSelectedOrgId(currentOrgId || null)

    api.getAdminOrganizations({ per_page: ORG_PICKER_PAGE_SIZE, sort_dir: 'asc' })
      .then((response) => {
        setOrganizations(response.data.data ?? response.data)
      })
      .catch((err) => {
        console.error('Failed to load organizations:', err)
        setError('Failed to load organizations')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isOpen])

  const handleConfirm = async () => {
    if (!plan || !selectedOrgId) return

    setSaving(true)
    setError(null)

    try {
      await api.changePlanOrganization(plan.token, selectedOrgId)
      onSuccess()
      onClose()
    } catch (err) {
      console.error('Failed to change organization:', err)
      setError(err.response?.data?.message || 'Failed to change organization')
    } finally {
      setSaving(false)
    }
  }

  if (!plan) return null

  const currentOrgName = plan.organization?.name || plan.created_by?.organization?.name

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-screen-sm">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <i className="fa fa-building text-blue-600 text-xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Change Organization</h2>
        </div>

        <div className="mb-4">
          <p className="text-gray-700 mb-1">
            Moving plan: <span className="font-semibold">{plan.name || "Untitled Plan"}</span>
          </p>
          {currentOrgName && (
            <p className="text-sm text-gray-500">
              Current organization: <span className="font-medium">{currentOrgName}</span>
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <i className="fa fa-spinner fa-spin mr-2"></i>
              Loading organizations...
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {organizations.map((org) => (
                <label
                  key={org.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                    selectedOrgId === org.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="organization"
                    value={org.id}
                    checked={selectedOrgId === org.id}
                    onChange={() => setSelectedOrgId(org.id)}
                    className="h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">{org.name}</span>
                    {org.id === currentOrgId && (
                      <span className="ml-2 text-xs text-gray-500">(current)</span>
                    )}
                  </div>
                </label>
              ))}
              {organizations.length === 0 && (
                <p className="text-sm text-gray-500 px-4 py-3">No organizations found</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary-ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={saving || loading || !selectedOrgId || selectedOrgId === currentOrgId}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ChangePlanOrganizationModal
